import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  getHeaders,
  handleError,
  handleResponse,
  handleFetchImageResponse,
  handleUploadResponse,
  handleUniqueNameCheckApiResponse,
} from './service-utilities';
import { CONFIG } from 'src/helper/app-config';
import {
  GetPlanogramBodyParams,
  PlanogramBaseDetailModal,
} from 'src/modals/planogram';

@Injectable({
  providedIn: 'root',
})
export class PlanogramService {
  constructor(private http: HttpClient) {}

  getSKUsList(planogram_id: number, _filter: string, _scenario: string) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.SKUGETALL,
        { planogram_id: planogram_id },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  fetchImageFromBlob(blobUrl: any) {
    let api_base_url = environment.app_url+'/fetch';
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'blob' as const,
    };

    return this.http
      .post(api_base_url, { blobUrl }, options )
      .pipe(map(handleFetchImageResponse), catchError(handleError));
  }

  uploadImage(formData: FormData) {
    let api_base_url = environment.app_url+'/upload';
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(`${api_base_url}`, formData)
      .pipe(map(handleUploadResponse), catchError(handleError));
  }

  createBasePlanogram(payload: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.CREATEBASEPLANOGRAM,
        payload,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getGuidelinesInput(payload: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.GETGUIDELINESINPUT,
        payload,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getCoolerInfo(payload: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.GETCOOLERINFO,
        payload,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getPicosSKUsList(planogram_id: number, _filter: string, _scenario: string) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.GETPICOSSKUS,
        { planogram_id: planogram_id },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getLhsSKUsList(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(environment.api_base_url + CONFIG.API.GETLHSSKULIST, info, options)
      .pipe(map(handleResponse), catchError(handleError));
  }

  getUserInfo() {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(environment.api_base_url + CONFIG.API.GETUSERINFO, {}, options)
      .pipe(map(handleResponse), catchError(handleError));
  }

  saveBottlerQueries(payload: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.SAVE_BOTTLER_QUERY,
        payload,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  saveCustomPlanogram(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.SAVECUSTOMPLANOGRAM,
        info,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getSpecificSku(id: number) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };

    return this.http
      .post(
        environment.api_base_url + CONFIG.API.GETSPECIFICSKU,
        { id: id },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getSpecificPlanogram(id: number) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };

    return this.http
      .post(
        environment.api_base_url + CONFIG.API.GETSPECIFICPLANOGRAM,
        { id: id },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  verifyPlanogramuniqueName(name: string) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.CHECKUNIQUEPLANOGRAM,
        { planogram_name: name },
        options
      )
      .pipe(map(handleUniqueNameCheckApiResponse), catchError(handleError));
  }

  getNPDdata(body_params: {}) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };

    return this.http
      .post(environment.api_base_url + CONFIG.API.GETNPD, body_params, options)
      .pipe(map(handleResponse), catchError(handleError));
  }

  getGuidelinesLibraryData() {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };

    return this.http
      .post(
        environment.api_base_url + CONFIG.API.GETGUIDELINESLIBRARYDATA,
        {},
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getVersionedGuidelines(payload: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };

    return this.http
      .post(
        environment.api_base_url + CONFIG.API.GETVERSIONEDGUIDELINES,
        payload,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getbasedata() {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.GETPLANOGRAMBASEDATA,
        {},
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getAllPlanograms() {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(environment.api_base_url + CONFIG.API.GETPLANOGRAMS, {}, options)
      .pipe(map(handleResponse), catchError(handleError));
  }

  getComparablePlanograms() {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.COMPARABLEPLANOGRAMS,
        {},
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  savePlanogramBaseDetails(body_params: PlanogramBaseDetailModal) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.SAVEPLANAGRAM,
        JSON.stringify(body_params),
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  editPlanogramBaseDetails(body_params: PlanogramBaseDetailModal) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.SAVEPLANAGRAM,
        JSON.stringify(body_params),
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  setUpSaveAsDraftPlanogramBaseDetails(body_params: PlanogramBaseDetailModal) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.SETUPSAVEASDRAFT,
        body_params,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getReviewSkuFacings(body_params: object) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.REVIEWSKUFACINGS,
        body_params,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  deletePlanogram(id: number) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.DELETEPLANAGRAM,
        { id: id.toString() },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getPlanogramSavedData(id: number) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.GETPLANOGRAMSAVEDBASEDATA,
        { id: id },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  setLockedGuidelines(payload: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.SETLOCKEDGUIDELINESL,
        payload,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  createPlanogramVersion(payload: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.CREATEPLANOGRAMVERSION,
        payload,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getCoolerTemplateBaseData(id: number) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.COOLERTEMPLATEBASEDATA,
        { planogram_id: id },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  incorporate_npds(payload: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.INCORPORATENPDS,
        payload,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  updatePlanogram(payload: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.UPDATEPLANOGRAM,
        payload,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getAdminBaseData(payload: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.ADMINFETCHBASEDATA,
        payload,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  uploadAdminBaseData(payload: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(environment.api_base_url + CONFIG.API.ADMINUPLOADBASEDATA, payload)
      .pipe(map(handleUploadResponse), catchError(handleError));
  }

  getSpecificScreenData(keys: string[]) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.SEPECIFICSCREENCONFIG,
        { key: keys },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  verifyTemplateUniqueName(name: string, id: string) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.CHECKUNIQUETEMPLATE,
        { template_name: name, planogram_id: id },
        options
      )
      .pipe(map(handleUniqueNameCheckApiResponse), catchError(handleError));
  }

  saveCoolerTemplate(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.SAVETEMPLATE,
        JSON.stringify(info),
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getAdminCoolerData() {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .get(environment.api_base_url + CONFIG.API.ADMINGETCOOLERDATA, options)
      .pipe(map(handleResponse), catchError(handleError));
  }

  getAdminSkuData() {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .get(environment.api_base_url + CONFIG.API.ADMINGETSKUDATA, options)
      .pipe(map(handleResponse), catchError(handleError));
  }

  uploadAdminCooler(file: any) {
    let headers: any = getHeaders();
    let options = {
      headers: headers,
      responseType: 'json' as 'json', // Update responseType to 'json'
    };
    return this.http
      .post(environment.api_base_url + CONFIG.API.ADMINPANELCOOLERUPLOAD, file)
      .pipe(map(handleUploadResponse), catchError(handleError));
  }

  uploadAdminSku(file: any) {
    let headers: any = getHeaders();
    let options = {
      headers: headers,
      responseType: 'json' as 'json', // Update responseType to 'json'
    };
    return this.http
      .post(environment.api_base_url + CONFIG.API.ADMINPANELSKUUPLOAD, file)
      .pipe(map(handleUploadResponse), catchError(handleError));
  }

  editCoolerTemplate(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.EDITTEMPLATE,
        JSON.stringify(info),
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getSavedCoolerTemplateData(template_id: number) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.GETSAVEDTEMPLATEDATA,
        { id: template_id },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getAllTemplates(id: number) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.GETALLTEMPLATES,
        { planogram_id: id },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  saveTemplateToPlanogram(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.SAVETEMPLATETOPLANOGRAM,
        JSON.stringify(info),
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  editCoolerFacing(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.EDITCOOLERFACING,
        JSON.stringify(info),
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  savePlanogramSkuPalcement(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.SAVEPLANOGRAMSKUPLACEMENT,
        info,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getReviewKpis(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(environment.api_base_url + CONFIG.API.GETREVIEWKPIS, info, options)
      .pipe(map(handleResponse), catchError(handleError));
  }

  createCustomPlanogram(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.CREATECUSTOMPLANOGRAM,
        info,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  fetchCustomPlanograms(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.FETCHCUSTOMPLANOGRAMS,
        info,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }
  updateMinimumFacings(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.UPDATEMINIMUMFACINGS,
        info,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  publishPlanogram(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.PUBLISHPLANOGRAM,
        info,
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  comparePlanogram(ids: number[]) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.COMPAREPLANOGRAM,
        { planogram_ids: ids },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  autoFillSKUS(planogram_id: number) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.AUTOFILLSKUS,
        { planogram_id: planogram_id },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getRecommandations(id: number) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.RECOMMANDATIONS,
        { planogram_id: id },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getAdditionalInfo(planogram_id: number) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.ADDITIONALINFO,
        { planogram_id: planogram_id },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  calculateThroughput(info: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.CALCULATETHROUGHPUT,
        JSON.stringify(info),
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  verticalAutoFillSKUS(planogram_id: number, block_config: any) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.VERTICALAUTOFILL,
        {
          planogram_id: planogram_id,
          vertical_brand_association: block_config,
        },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  copyPlanogram(planogram_id: number, planogram_name: string) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.COPYPLANOGRAM,
        { planogram_id: planogram_id, planogram_name: planogram_name },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getSavedPlanogramInfo(user_id: number) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.GETSAVEDPLANOGRAMINFO,
        { user_id: user_id },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getAdminUpdateData() {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(environment.api_base_url + CONFIG.API.ADMINUPDATEDATA, {}, options)
      .pipe(map(handleResponse), catchError(handleError));
  }

  getAdminUserAnalyticsData() {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.ADMINUSERANALYTICS,
        {},
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }

  getAdminUserQueriesData() {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(environment.api_base_url + CONFIG.API.ADMINUSERQUERIES, {}, options)
      .pipe(map(handleResponse), catchError(handleError));
  }

  markResolveUserQuery(query_id: number, user_id: number) {
    let headers = getHeaders();
    let options = {
      headers: headers,
      responseType: 'text' as 'text',
    };
    return this.http
      .post(
        environment.api_base_url + CONFIG.API.ADMINMARKRESOLVE,
        {
          query_id: query_id,
          user_id: user_id,
        },
        options
      )
      .pipe(map(handleResponse), catchError(handleError));
  }
}
